import { useState, useEffect } from 'react';
import { RefractionTest, RefractionResults } from '..';
import ExamEyeSetup from '../exams/ExamEyeSetup';
import { useTestContext } from '../../../context/testContext';
import { usePostMessage } from '../../../context/messageContext';
import { ibisVisionToolOptions } from '../../../helpers/refraction';
import { PanelTitle, PanelText, SessionActions } from '../../../ui';
import { StepBody, useTestStepSync, useConfirmLeave } from '../testScreenKit';

const ChangeEyePrompt = ({ updateStep }) => {
  const confirmLeave = useConfirmLeave();
  return (
    <StepBody center data-testid='refraction-change-eye'>
      <PanelTitle>Now prompt the patient to move to the next eye.</PanelTitle>
      <PanelText>Click "Next" to proceed to the next eye test when you are ready.</PanelText>
      <SessionActions onBack={() => confirmLeave().then(() => updateStep('rightEyeTest'))} primary={{ label: 'Next', onClick: () => updateStep('leftEyeTest') }} />
    </StepBody>
  );
};

const Refraction = ({ setTitle, setActiveTab }) => {
  const { testData, updateTestData } = useTestContext();
  const [step, setStep] = useState('eyeSetup');
  const postMessage = usePostMessage();
  useTestStepSync(testData.testKey, step);

  useEffect(() => {
    switch (step) {
      case 'refractionTestResult':
        return setTitle('Refraction Test');
      case 'eyeSetup':
        return setTitle('Refraction Test');
      case 'rightEyeTest':
        return setTitle('Refraction Test - Right Eye');
      case 'changeEyePrompt':
        return setTitle('Refraction Test - Change Eye');
      case 'leftEyeTest':
        if (testData.testRightEye) updateTestData({ testInProgress: true });
        return setTitle('Refraction Test - Left Eye');
      case 'bothEyesTest':
        return setTitle('Refraction Test - Binocular');
      case 'results':
        return setTitle('Refraction - Results');
      default:
        break;
    }
  }, [setTitle, step, updateTestData, testData.testLeftEye, testData.testRightEye]);

  useEffect(() => {
    if (!step.endsWith('Setup') || step.startsWith('eye')) return;
    const eyeTested = step.split('Setup')[0];
    updateTestData({ [eyeTested]: { selectedTool: ibisVisionToolOptions[0] } });
    updateStep(`${eyeTested}Test`);
  }, [updateTestData, step]);

  const updateStep = (selectedStep) => {
    if (selectedStep === 'results') postMessage({ type: 'endOfTest' });
    if (selectedStep === 'selectTest') postMessage({ type: 'holding' });
    if (selectedStep.endsWith('Test')) postMessage({ type: 'startRefractionTwo' });
    return setStep(selectedStep);
  };

  const renderSwitch = () => {
    switch (step) {
      case 'eyeSetup':
        return <ExamEyeSetup updateStep={updateStep} />;
      case 'changeEyePrompt':
        return <ChangeEyePrompt updateStep={updateStep} />;
      case 'rightEyeTest':
      case 'leftEyeTest':
      case 'bothEyesTest':
        return <RefractionTest eyeTested={step.split('T')[0]} updateStep={updateStep} />;
      case 'results':
        return <RefractionResults localResults={testData} setActiveTab={setActiveTab} updateStep={updateStep} />;
      default:
        return;
    }
  };

  return (
    <StepBody data-testid='refraction' data-step={step}>
      {renderSwitch()}
    </StepBody>
  );
};

export default Refraction;
